import { ForwardedRef, forwardRef } from "react";

import { StreamingStateProvider } from "../../contexts/StreamingState/Provider";
import { UserActivityProvider } from "../../contexts/UserActivity/Provider";
import { VideoPlayerRefsProvider } from "../../contexts/VideoPlayerRefs/Provider";

import {
  ControlBarProps,
  ControlBarProvider,
} from "../../contexts/ControlBar/Provider";
import { SettingsProvider } from "../../contexts/Settings/Provider";

import { MarkersProps, MarkersProvider } from "../../contexts/Markers/Provider";
import { RootProps, RootProvider } from "../../contexts/Root/Provider";
import {
  PlaybackRateProps,
  PlaybackRateProvider,
} from "../../contexts/PlaybackRate/Provider";
import {
  VideoSourceProps,
  VideoSourceProvider,
} from "../../contexts/VideoSource/Provider";
import { SeekBarProps, SeekBarProvider } from "../../contexts/SeekBar/Provider";

import { RangeProps, RangeProvider } from "../../contexts/Range/Provider";
import { LayoutProps, LayoutProvider } from "../../contexts/Layout/Provider";
import { BaseProps, BaseVideoPlayer } from "./BaseVideoPlayer";

import "@fontsource/roboto/400.css";

export interface Props
  extends RootProps,
    LayoutProps,
    BaseProps,
    VideoSourceProps,
    ControlBarProps,
    SeekBarProps,
    RangeProps,
    MarkersProps,
    PlaybackRateProps {}

export const VideoPlayer = forwardRef(
  (props: Props, ref: ForwardedRef<HTMLVideoElement>) => {
    return (
      <RootProvider
        aspectRatio={props.aspectRatio}
        loop={props.loop}
        autoplay={props.autoplay}
        label={props.label}
        poster={props.poster}
        theme={props.theme}
        controls={props.controls}
        setClipRange={props.setClipRange}
        clippingEnabled={props.clippingEnabled}
        setClippingEnabled={props.setClippingEnabled}
        clipRange={props.clipRange}
      >
        <VideoPlayerRefsProvider forwardedRef={ref}>
          <StreamingStateProvider src={props.src}>
            <VideoSourceProvider
              src={props.src}
              startTime={props.startTime}
              transitionDelay={props.transitionDelay}
            >
              <RangeProvider range={props.range}>
                <SettingsProvider>
                  <PlaybackRateProvider playbackRates={props.playbackRates}>
                    <UserActivityProvider>
                      <LayoutProvider layout={props.layout}>
                        <ControlBarProvider
                          controlBarActiveCallback={
                            props.controlBarActiveCallback
                          }
                        >
                          <SeekBarProvider
                            seekBarPointerStateCallback={
                              props.seekBarPointerStateCallback
                            }
                          >
                            <MarkersProvider
                              transitionDelay={props.transitionDelay}
                              markers={props.markers}
                            >
                              <BaseVideoPlayer
                                listenKeyboard={props.listenKeyboard}
                                listenPointer={props.listenPointer}
                              >
                                {props.children}
                              </BaseVideoPlayer>
                            </MarkersProvider>
                          </SeekBarProvider>
                        </ControlBarProvider>
                      </LayoutProvider>
                    </UserActivityProvider>
                  </PlaybackRateProvider>
                </SettingsProvider>
              </RangeProvider>
            </VideoSourceProvider>
          </StreamingStateProvider>
        </VideoPlayerRefsProvider>
      </RootProvider>
    );
  }
);
